import { Controller } from "@hotwired/stimulus";
import Hls from "hls.js";

export default class extends Controller {
  static targets = ["slide", "slideWrapper", "progressBar", "muteIcon", "unmuteIcon"];

  connect() {
    this.currentIndex = 0;
    this.paused = false;
    this.muted = true;
    this.isActive = false;
    this.hlsInstances = new Map();

    this.boundEnded = this.handleEnded.bind(this);
    this.boundTimeUpdate = this.handleTimeUpdate.bind(this);
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this);

    this.slideTargets.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.addEventListener("ended", this.boundEnded);
      video.addEventListener("timeupdate", this.boundTimeUpdate);
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activate();
          } else {
            this.deactivate();
          }
        });
      },
      { threshold: 0.1 },
    );
    this.observer.observe(this.element);

    document.addEventListener("visibilitychange", this.boundVisibilityChange);
  }

  disconnect() {
    this.observer?.disconnect();
    document.removeEventListener("visibilitychange", this.boundVisibilityChange);
    this.slideTargets.forEach((video) => {
      video.removeEventListener("ended", this.boundEnded);
      video.removeEventListener("timeupdate", this.boundTimeUpdate);
      video.pause();
    });
    this.destroyAllHls();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else if (this.isActive) {
      this.resume();
    }
  }

  activate() {
    if (this.isActive) return;
    this.isActive = true;
    this.show(this.currentIndex);
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    this.slideTargets.forEach((v) => v.pause());
    this.destroyAllHls();
  }

  destroyAllHls() {
    this.hlsInstances.forEach((hls) => hls.destroy());
    this.hlsInstances.clear();
    this.slideTargets.forEach((v) => {
      v.dataset.loaded = "false";
      try {
        v.removeAttribute("src");
        v.load();
      } catch (_) {
        // ignore
      }
    });
  }

  loadVideo(video) {
    if (video.dataset.loaded === "true") return;

    const url = video.dataset.hlsUrl;
    if (!url) return;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
    } else if (Hls.isSupported()) {
      const hls = new Hls({ maxBufferLength: 10, maxMaxBufferLength: 20 });
      hls.loadSource(url);
      hls.attachMedia(video);
      this.hlsInstances.set(video, hls);
    }

    video.dataset.loaded = "true";
  }

  show(index) {
    this.currentIndex = index;

    const wrappers = this.hasSlideWrapperTarget ? this.slideWrapperTargets : this.slideTargets;
    wrappers.forEach((wrapper, i) => {
      if (i === index) {
        wrapper.style.opacity = "1";
        wrapper.style.zIndex = "1";
      } else {
        wrapper.style.opacity = "0";
        wrapper.style.zIndex = "0";
      }
    });

    this.progressBarTargets.forEach((bar, i) => {
      bar.style.transition = "none";
      bar.style.width = i < index ? "100%" : "0%";
    });

    if (!this.isActive) return;

    const currentVideo = this.slideTargets[index];
    this.loadVideo(currentVideo);
    try {
      currentVideo.currentTime = 0;
    } catch (_) {
      // currentTime can't be set before metadata loads — fine
    }
    if (!this.paused) {
      currentVideo.play().catch(() => {});
    }

    this.slideTargets.forEach((video, i) => {
      if (i !== index) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (_) {
          // ignore
        }
      }
    });

    const nextIndex = (index + 1) % this.slideTargets.length;
    if (nextIndex !== index) {
      this.loadVideo(this.slideTargets[nextIndex]);
    }
  }

  handleTimeUpdate(event) {
    const video = event.currentTarget;
    const index = this.slideTargets.indexOf(video);
    if (index !== this.currentIndex) return;

    if (video.duration && video.duration > 0) {
      const progress = (video.currentTime / video.duration) * 100;
      const bar = this.progressBarTargets[index];
      if (bar) bar.style.width = `${progress}%`;
    }
  }

  handleEnded(event) {
    const video = event.currentTarget;
    const index = this.slideTargets.indexOf(video);
    if (index !== this.currentIndex) return;
    this.next();
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slideTargets.length;
    this.show(nextIndex);
  }

  previous() {
    const prevIndex = (this.currentIndex - 1 + this.slideTargets.length) % this.slideTargets.length;
    this.show(prevIndex);
  }

  goTo(event) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    if (target.dataset.direction === "next") {
      this.next();
    } else {
      this.previous();
    }
  }

  pause() {
    this.paused = true;
    const video = this.slideTargets[this.currentIndex];
    if (video) video.pause();
  }

  resume() {
    this.paused = false;
    if (!this.isActive) return;
    const video = this.slideTargets[this.currentIndex];
    if (video) video.play().catch(() => {});
  }

  toggleMute(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.muted = !this.muted;
    this.slideTargets.forEach((video) => {
      video.muted = this.muted;
    });
    if (this.hasMuteIconTarget) this.muteIconTarget.style.display = this.muted ? "" : "none";
    if (this.hasUnmuteIconTarget) this.unmuteIconTarget.style.display = this.muted ? "none" : "";
  }
}
